import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";
import { blankAgent, toBody, useRoles } from "./agentUtil";
import { IdentityTab } from "./IdentityTab";
import { ModelTab } from "./ModelTab";
import { PersonaTab } from "./PersonaTab";
import { AvatarTab } from "./AvatarTab";
import { PreviewTab } from "./PreviewTab";

const TABS = [["identity", "Identitas & Peran"], ["model", "Model & Suara"], ["persona", "Persona & Knowledge"], ["avatar", "Avatar Realtime"], ["preview", "Pratinjau"]];

export default function AgentEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const { role } = useOutletContext() || {};
  const readOnly = role !== "super_admin";
  const nav = useNavigate();
  const [roles] = useRoles();
  const [a, setA] = useState(null);
  const [orig, setOrig] = useState("");
  const [rt, setRt] = useState(null);
  const [tab, setTab] = useState("identity");
  const [busy, setBusy] = useState(false);

  const accept = (doc) => { setA(doc); setOrig(JSON.stringify(toBody(doc))); };
  const load = useCallback(() => api.get(`/platform/support-agents/${id}`).then((r) => accept(r.data)).catch((e) => { toast.error(apiErr(e, "Agent tidak ditemukan")); nav("..", { relative: "path" }); }), [id, nav]);
  useEffect(() => { api.get("/platform/models/realtime-avatar").then((r) => setRt(r.data)).catch(() => setRt(null)); }, []);
  useEffect(() => { if (!isNew) load(); }, [isNew, load]);
  useEffect(() => { if (isNew && roles && !a) { const b = blankAgent(roles.find((r) => r.key === "general" && r.enabled) || roles.find((r) => r.enabled)); setA(b); setOrig(JSON.stringify(toBody(b))); } }, [isNew, roles, a]);

  const dirty = useMemo(() => a && JSON.stringify(toBody(a)) !== orig, [a, orig]);
  useEffect(() => { if (!dirty) return undefined; const h = (e) => { e.preventDefault(); e.returnValue = ""; }; window.addEventListener("beforeunload", h); return () => window.removeEventListener("beforeunload", h); }, [dirty]);
  const set = (patch) => setA((x) => ({ ...x, ...(typeof patch === "function" ? patch(x) : patch) }));
  // Portrait upload/reset is persisted server-side immediately: merge only portrait fields, keep other unsaved edits.
  const onPortrait = async (d) => {
    const src = d && "portrait" in d ? d : (await api.get(`/platform/support-agents/${id}`)).data;
    const patch = { portrait: src.portrait || "", portrait_custom: !!src.portrait_custom };
    setA((x) => ({ ...x, ...patch })); setOrig((o) => JSON.stringify({ ...JSON.parse(o), portrait: patch.portrait }));
  };

  const err = !a ? "" : !a.name?.trim() || a.name.length > 40 ? "Nama wajib (1–40 karakter)" : a.role_key === "custom" && !a.role_label?.trim() ? "Label peran kustom wajib diisi" : (a.summary || "").length > 120 ? "Ringkasan maks 120 karakter" : "";
  const save = async () => {
    setBusy(true);
    try {
      const body = toBody(a); if (a.role_key !== "custom") body.role_label = null;
      if (isNew) { const r = await api.post("/platform/support-agents", body); toast.success("Agent dibuat"); setOrig(JSON.stringify(toBody(r.data))); nav(`../${r.data.id}`, { relative: "path", replace: true }); }
      else { const r = await api.put(`/platform/support-agents/${id}`, body); accept(r.data); toast.success("Agent disimpan"); }
    } catch (e) { toast.error(apiErr(e, "Gagal menyimpan agent")); } finally { setBusy(false); }
  };
  const makeDefault = async () => { setBusy(true); try { await api.post(`/platform/support-agents/${id}/default`); toast.success("Dijadikan default"); load(); } catch (e) { toast.error(apiErr(e, "Gagal")); } finally { setBusy(false); } };
  const back = () => { if (!dirty || window.confirm("Perubahan belum disimpan. Tinggalkan halaman?")) nav("..", { relative: "path" }); };

  if (!a || !roles) return <Loader2 className="animate-spin text-slate-400" />;
  const props = { a, set, readOnly, roles, rt, isNew, onPortrait };
  return (
    <div className="pb-24" data-testid="agent-editor">
      <button onClick={back} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900" data-testid="agent-back"><ArrowLeft size={14} /> Semua agent</button>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-black tracking-tight text-slate-900" data-testid="agent-editor-title">{isNew ? "Agent baru" : a.name || "Tanpa nama"}</h1>
        {a.is_default && <span className="rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold text-white">Default</span>}
        {!isNew && <span className="font-mono text-xs text-slate-400">{a.id}</span>}
        {!isNew && !readOnly && !a.is_default && <button onClick={makeDefault} disabled={busy || !a.enabled || dirty} title={!a.enabled ? "Aktifkan & simpan dulu" : ""} className="btn-soft ml-auto inline-flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs font-bold disabled:opacity-40" data-testid="agent-make-default"><Star size={13} /> Jadikan default</button>}
      </div>
      {readOnly && <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">Mode baca — hanya Super Admin yang dapat mengubah agent.</p>}
      <div className="tabs mt-5">{TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`tab ${tab === k ? "tab-active" : ""}`} data-testid={`agent-tab-${k}`}>{l}</button>)}</div>
      <div className="mt-6">
        {tab === "identity" && <IdentityTab {...props} />}
        {tab === "model" && <ModelTab {...props} />}
        {tab === "persona" && <PersonaTab {...props} />}
        {tab === "avatar" && <AvatarTab {...props} />}
        {tab === "preview" && <PreviewTab {...props} dirty={dirty} />}
      </div>
      {!readOnly && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white/90 backdrop-blur lg:left-60" data-testid="agent-savebar">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-end gap-3 px-4 py-3">
            {err ? <span className="text-xs font-semibold text-rose-600" data-testid="agent-error">{err}</span> : dirty && <span className="text-xs font-semibold text-amber-600" data-testid="agent-dirty">Perubahan belum disimpan</span>}
            {dirty && !isNew && <button onClick={() => setA({ ...a, ...JSON.parse(orig) })} className="btn-soft rounded-xl px-4 py-2 text-xs font-bold" data-testid="agent-reset">Batalkan</button>}
            <button onClick={save} disabled={busy || !dirty || !!err} className="btn-primary inline-flex items-center gap-1.5 rounded-xl px-5 py-2 text-xs font-bold disabled:opacity-50" data-testid="agent-save">{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {isNew ? "Buat agent" : "Simpan"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
